import {  Button, IconButton, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useState, useEffect, useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import { useGetStoreListQuery, useDeleteStoreMutation, useUpdateStoreStatusMutation } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";

const ManageStores = () => {

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [Status, setStatus] = React.useState('default');
  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Fetch stores from API
  const { data, isLoading, error, refetch } = useGetStoreListQuery({
    page,
    limit,
    search: debouncedSearchTerm,
  });

  // Delete store mutation
  const [deleteStore, { isLoading: isDeleting }] = useDeleteStoreMutation();

  // Update store status mutation
  const [updateStoreStatus, { isLoading: isUpdatingStatus }] = useUpdateStoreStatusMutation();

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState<{ id: string; name: string } | null>(null);

  // Extract stores from API response
  const rows = useMemo(() => {
    if (data?.data?.store && Array.isArray(data.data.store)) {
      return data.data.store.map((store: any) => {
        // Get current day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
        const currentDay = new Date().getDay();
        
        // Find the weekday object that matches the current day
        let isTodayActive = false;
        if (store.storeAvailability) {
          // Loop through all weekday keys to find the one matching current day
          const weekdayKeys = Object.keys(store.storeAvailability);
          for (const key of weekdayKeys) {
            const weekdayData = store.storeAvailability[key];
            if (weekdayData && weekdayData.day === currentDay) {
              isTodayActive = weekdayData.isActive === true;
              break;
            }
          }
        }

        return {
          id: store._id || store.id,
          name: store.name || "N/A",
          code: store.code || "N/A",
          status: isTodayActive ? "Open" : "Closed",
          address: store.address?.fullAddress || store.address?.street || "N/A",
          region: store.region || store.address?.city || "N/A",
          date: store.createdAt
            ? new Date(store.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
          isBlocked: store.isBlocked || false,
          storeAvailability: store.storeAvailability,
        };
      });
    }
    return [];
  }, [data]);

  // Filter rows based on status selection
  const filteredRows = useMemo(() => {
    if (Status === "default" || Status === "All") {
      return rows;
    }
    return rows.filter((row) => row.status === Status);
  }, [rows, Status]);

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || "Failed to fetch stores. Please try again.";
      showError(errorMessage);
    }
  }, [error]);

  // Handle status toggle (Enable/Disable)
  const handleStatusToggle = async (storeId: string, storeName: string, currentStatus: boolean) => {
    const newStatus = !currentStatus; // isBlocked: false means enabled/open, true means disabled/closed
    
    try {
      const response = await updateStoreStatus({ 
        id: storeId, 
        isBlocked: newStatus 
      }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || 
        `Store "${storeName}" ${newStatus ? "disabled" : "enabled"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating store status:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update store status. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete click
  const handleDeleteClick = (store: any) => {
    setStoreToDelete({ id: store.id, name: store.name });
    setDeleteModalOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!storeToDelete) return;

    try {
      const response = await deleteStore({ id: storeToDelete.id }).unwrap();
      const successMessage = response?.message || `Store "${storeToDelete.name}" deleted successfully`;
      showToast(successMessage);
      setDeleteModalOpen(false);
      setStoreToDelete(null);
      refetch();
    } catch (error: any) {
      console.error("Error deleting store:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete store. Please try again.";
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus} />
      <div className="main_title">
        <h1>Store Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Store Management
        </p>
      </div>
      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search..."
            />
            <div className=" form">
              <Select
                fullWidth
                value={Status}
                onChange={handleStatusChange} >
                <MenuItem disabled value="default">Select Status</MenuItem>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </div>
          </div>
          <div className="right_s">
            <Button className="btn btn_black"><FileUploadIcon /> Bulk Upload</Button>
            <Button className="btn btn_primary" onClick={() => navigate("/manage-stores/add")}><AddIcon /> Add Store</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Store Code</TableCell>
                <TableCell>Store Name</TableCell>
                <TableCell>Address / Region</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Disable/Enable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading stores...
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No stores found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      <a 
                        href="/manage-stores/detail" 
                        className="click_action" 
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/manage-stores/detail`, { state: { storeId: row.id } });
                        }}
                      >
                        <b>{row.code}</b>
                      </a>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.address}, {row.region}</TableCell>
                    <TableCell>
                      <span
                        className={`status_btn dark ${row.status === "Open"
                          ? "c_success"
                          : row.status === "Closed"
                            ? "c_danger"
                            : ""
                          }`}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={!row.isBlocked}
                        onChange={() => handleStatusToggle(row.id, row.name, row.isBlocked)}
                        disabled={isUpdatingStatus || isLoading}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Button className="btn btn_primary" onClick={() => navigate(`/manage-stores/inventory?storeId=${row.id}`, { state: { storeId: row.id } })}>Manage Inventory</Button>
                        <IconButton onClick={() => navigate(`/manage-stores/detail`, { state: { storeId: row.id } })}>
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>
                        <IconButton 
                          onClick={() => navigate("/manage-stores/add", { state: { storeId: row.id } })}
                          disabled={isLoading}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(row)}
                          disabled={isLoading || isDeleting}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              {Status !== "default" && Status !== "All" && (
                <span> ({filteredRows.length} {Status.toLowerCase()})</span>
              )}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Pagination
                module={filteredRows}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setStoreToDelete(null);
        }}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        itemName={storeToDelete?.name || "this store"}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageStores;
